import { Formik } from "formik";
import { Form } from "react-bootstrap";
import React, { useState } from "react";
import * as yup from "yup";
import SecurityCodeInput from "./SecurityCodeInput";
import AppButton from "../../views/components/Common/AppButton";
import useJwt from "../../services/useJwt";
import Message from "../../utility/notifications/Message";
import { TwoFactorPreference } from "../../types/apiTypes";
import AppTextInput from "../../views/components/forms/AppTextInput";

const schema = yup.object().shape({
  twoFactorVerificationCode: yup.string().required().label("2FA code"),
});

interface TwoFactorVerificationProps {
  username: string;
  password: string;
  twoFactorType: TwoFactorPreference;
  rememberClient?: boolean;
  onSuccess: (data: any) => void;
  onBack: () => void;
  showPasswordUpdateFlow: boolean
}

const TwoFactorVerification = ({
  username,
  password,
  twoFactorType,
  rememberClient = false,
  onSuccess,
  onBack,
 showPasswordUpdateFlow
}: TwoFactorVerificationProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);

    try {
      const response = await useJwt.login({
        userNameOrEmailAddress: username,
        password,
        newPassword: values.newPassword,
        twoFactorVerificationCode: values.twoFactorVerificationCode,
        twoFactorRememberClient: rememberClient,
      });

      if (response.status === 200) {
        onSuccess(response.data);
      }
    } catch (err: any) {
      Message(
        err.response?.data?.error?.message || "Invalid verification code",
        err.response?.data?.error?.details || "",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    switch (twoFactorType) {
      case TwoFactorPreference.Value0:
        return "Enter Email Verification Code";
      case TwoFactorPreference.Value1:
        return "Enter Authenticator Code";
      default:
        return "Enter Verification Code";
    }
  };

  const getDescription = () => {
    switch (twoFactorType) {
      case TwoFactorPreference.Value0:
        return "Please check your email and enter the verification code sent to you.";
      case TwoFactorPreference.Value1:
        return "Please open your authenticator app and enter the current verification code.";
      default:
        return "Please enter your verification code to continue.";
    }
  };

  const getInputLabel = () => {
    switch (twoFactorType) {
      case TwoFactorPreference.Value0:
        return "Enter the code from your email";
      case TwoFactorPreference.Value1:
        return "Enter the code from your authenticator app";
      default:
        return "Enter verification code";
    }
  };

  return (
    <>
      <div className="my-4">
        <div className="text-center mb-4">
          <AppButton
            type="button"
            title="Back to login"
            buttonSize="sm"
            variant="back"
            onClick={onBack}
          />
        </div>

        <h1 className="h3 mb-3 fw-bold text-primary text-center">
          {getTitle()}
        </h1>
        <p className="text-center text-muted mb-4">{getDescription()}</p>

        {twoFactorType === TwoFactorPreference.Value0 && (
          <div className="alert alert-info text-center">
            <i className="fas fa-envelope me-2"></i>A verification code has been
            sent to your email address.
          </div>
        )}
      </div>

      <Formik
        enableReinitialize={true}
        initialValues={{
          twoFactorVerificationCode: "",
        }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit }) => (
            <Form className="auth-login-form mt-2" onSubmit={handleSubmit}>
              <div className="mb-4">
                <SecurityCodeInput
                    name="twoFactorVerificationCode"
                    label={getInputLabel()}
                />
              </div>

              {
                  showPasswordUpdateFlow && <>
                  <h1 className="h3 mb-3 fw-bold text-primary text-center">
                    Password expired
                  </h1>
                  <p className="text-center text-muted mb-4">Your password has expired. Please enter a new password
                    here</p>

                  <div className="mb-4">
                    <AppTextInput
                        required
                        type={"password"}
                        name={"newPassword"}
                        label={'Enter a new password here'}
                    />
                  </div>
                </>
              }

              <AppButton
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  type="submit"
                  title="Verify and Login"
                  buttonSize="lg"
                  className="w-100"
              />
            </Form>
        )}
      </Formik>
    </>
  );
};

export default TwoFactorVerification;
